import { useId, useMemo, useState } from 'react';
import { Segmented } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';

import { Button, Dialog, Field, Input, Select, Skeleton, Textarea } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { cn } from '../../../lib/cn';
import { ngayVN, soVN } from '../../../lib/dinh-dang';
import { loiTheoTruong } from '../../../lib/errors';
import { useTienUsd, useTienVnd } from '../../goi-nap-token/components/GiaHienThi';
import { useGoiDangBan } from '../../goi-nap-token/hooks/useGoiNapToken';
import {
  chuanSoTien,
  giaVnd,
  kiemSoTien,
  THANG_HOP_LE,
  tokenTuSoTien,
} from '../../goi-nap-token/nghiep-vu/fox';
import type { TopUpOffer } from '../../goi-nap-token/types';
import { cauLoiChung, useLoiCapThem } from '../../tenant/components/loi-cap-them';
import { useNhanThoiHan } from '../../tenant/components/nhan-thoi-han';
import { ONgayTrongKhoang } from '../../tenant/components/ONgayTrongKhoang';
import { CanhBaoTrongHop, LoiTrongHop } from '../../tenant/components/phan-form';
import { useCapThemToken, useGuiYeuCauToken } from '../../tenant/hooks/useCapThemToken';
import {
  DO_DAI_GHI_CHU,
  goiTamThoiGiaHanDuoc,
  homNayVN,
  kiemYeuCau,
  YEU_CAU_CHO_TOI_DA,
} from '../../tenant/nghiep-vu/cap-them-token';
import type { AiTokenRequestCreate } from '../../tenant/types';

type CheDo = 'goi' | 'tien' | 'gia-han';

/** Con trỏ lỗi của server có ô để hiện ngay dưới. */
const TRUONG_CO_O = ['/package_code', '/amount_usd', '/duration_count', '/valid_to', '/reason', '/grant_id'];

/**
 * Hộp "Gửi yêu cầu nạp token" của Tenant Admin (ADR-0100 mục 5; `POST /api/v1/tenants/{id}/ai-token-requests`).
 *
 * - **Chọn gói**: gói nạp token đang bán, mỗi gói nói số token Fox, thời hạn, giá USD và ≈ VND theo tỷ giá hiện hành.
 * - **Nạp theo số tiền**: mức gợi ý hoặc số tự nhập, xem trước số token Fox = floor(số tiền / đơn giá × 1.000.000) và
 *   ≈ VND; thời hạn 1, 3, 6, 12 tháng. Nền tảng chưa đặt đơn giá thì chế độ này tắt, kèm một câu nói vì sao.
 * - **Gia hạn gói**: chỉ hiện khi tổ chức có gói tạm thời gia hạn được (ADR-0091, không đổi).
 *
 * Lý do tuỳ chọn khi nạp, bắt buộc khi gia hạn. Giá cuối cùng là ảnh chụp server ghi lúc nhận yêu cầu.
 */
export function HopGuiYeuCauToken({
  tenantId,
  daDuCho,
  onClose,
}: {
  tenantId: string;
  daDuCho: boolean;
  onClose: () => void;
}) {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const loiCapThem = useLoiCapThem();
  const nhanThoiHan = useNhanThoiHan();
  const tienUsd = useTienUsd();
  const tienVnd = useTienVnd();
  const formId = useId();
  const gui = useGuiYeuCauToken(tenantId);
  const danhMuc = useGoiDangBan(tenantId);
  const capThem = useCapThemToken(tenantId);
  const [khoaLap] = useState(() => crypto.randomUUID());

  const [cheDo, setCheDo] = useState<CheDo>('goi');
  const [maGoi, setMaGoi] = useState<string | null>(null);
  const [soTien, setSoTien] = useState('');
  const [soThang, setSoThang] = useState<number>(1);
  const [lyDo, setLyDo] = useState('');
  const [giaHan, setGiaHan] = useState({ grantId: '', validTo: '' });
  const [daGui, setDaGui] = useState(false);

  const homNay = homNayVN();
  const goiGiaHan = useMemo(() => goiTamThoiGiaHanDuoc(capThem.data ?? []), [capThem.data]);
  const cat = danhMuc.data;
  const donGia = cat?.don_gia_usd_moi_trieu_token_fox ?? null;
  const tyGia = cat?.exchange_rate?.rate ?? null;

  const loiServer = loiTheoTruong(gui.error);
  const loiChung = cauLoiChung(gui.error, loiServer, TRUONG_CO_O, dichLoi);
  const doi = (lam: () => void) => {
    lam();
    if (gui.error) gui.reset();
  };

  // Theo số tiền
  const tien = chuanSoTien(soTien);
  const loiTien = kiemSoTien(soTien);
  const tokenXemTruoc = !loiTien && donGia ? tokenTuSoTien(tien, donGia) : null;
  const vndXemTruoc = !loiTien ? giaVnd(Number(tien).toFixed(2), tyGia) : null;

  // Gia hạn: dùng lại luật của ADR-0091
  const kqGiaHan = kiemYeuCau(
    { kind: 'GIA_HAN', tokens: '', donVi: 'MONTH', soLuong: 1, validTo: giaHan.validTo, grantId: giaHan.grantId, reason: lyDo },
    homNay,
    (id) => goiGiaHan.find((g) => g.id === id)?.valid_to,
  );
  const hanGoiChon = goiGiaHan.find((g) => g.id === giaHan.grantId)?.valid_to;
  const lyDoDai = lyDo.trim().length > DO_DAI_GHI_CHU;

  const nop = () => {
    setDaGui(true);
    if (gui.isPending || lyDoDai) return;
    let than: AiTokenRequestCreate | null = null;
    const reason = lyDo.trim() || null;
    if (cheDo === 'goi' && maGoi) than = { package_code: maGoi, reason };
    if (cheDo === 'tien' && !loiTien && donGia) {
      than = { amount_usd: tien, duration_unit: 'MONTH', duration_count: soThang, reason };
    }
    if (cheDo === 'gia-han' && kqGiaHan.than) than = kqGiaHan.than;
    if (!than) return;
    gui.mutate({ du: than, khoaLap }, { onSuccess: onClose });
  };

  const cacCheDo = [
    { value: 'goi' as const, label: t('banToken.yeuCau.cheDoGoi') },
    { value: 'tien' as const, label: t('banToken.yeuCau.cheDoTien'), disabled: Boolean(cat) && donGia === null },
    ...(goiGiaHan.length > 0 ? [{ value: 'gia-han' as const, label: t('banToken.yeuCau.cheDoGiaHan') }] : []),
  ];

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('banToken.yeuCau.hopTieuDe')}
      width={600}
      destroyOnHidden
      footer={
        <div className="space-y-3">
          {loiChung && <LoiTrongHop>{loiChung}</LoiTrongHop>}
          <div className="flex flex-wrap justify-end gap-3">
            <Button variant="outline" size="md" onClick={onClose}>
              {t('chung.huy')}
            </Button>
            <Button variant="primary" size="md" htmlType="submit" form={formId} loading={gui.isPending}>
              {gui.isPending ? t('tokenAi.dangGui') : t('tokenAi.guiYeuCau')}
            </Button>
          </div>
        </div>
      }
    >
      <form
        id={formId}
        noValidate
        className="space-y-4 pt-2"
        onSubmit={(e) => {
          e.preventDefault();
          nop();
        }}
      >
        {daDuCho && <CanhBaoTrongHop>{t('tokenAi.daDuCho', { so: soVN(YEU_CAU_CHO_TOI_DA, locale) })}</CanhBaoTrongHop>}

        <Segmented<CheDo>
          block
          aria-label={t('banToken.yeuCau.cheDo')}
          options={cacCheDo}
          value={cheDo}
          onChange={(v) => doi(() => setCheDo(v))}
        />

        {danhMuc.isLoading && cheDo !== 'gia-han' && (
          <div className="space-y-2" aria-busy>
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        )}
        {danhMuc.error && !cat && cheDo !== 'gia-han' && (
          <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded border border-default px-3 py-2.5">
            <span className="min-w-0 text-sm text-danger-700">{t('banToken.yeuCau.loiTai', { loi: dichLoi(danhMuc.error) })}</span>
            <Button variant="outline" size="sm" icon={<ReloadOutlined />} onClick={() => void danhMuc.refetch()}>
              {t('chung.thuLai')}
            </Button>
          </div>
        )}

        {cat && cheDo === 'goi' && (
          <Field
            label={t('banToken.yeuCau.oGoi')}
            required
            error={!maGoi && daGui ? t('banToken.yeuCau.loiChonGoi') : loiServer['/package_code']}
          >
            {(p) =>
              cat.items.length === 0 ? (
                <p id={p.id} className="text-sm text-content-muted">
                  {t('banToken.yeuCau.chuaCoGoi')}
                </p>
              ) : (
                <div
                  role="radiogroup"
                  id={p.id}
                  aria-invalid={p['aria-invalid']}
                  aria-describedby={p['aria-describedby']}
                  className="grid gap-2 sm:grid-cols-2"
                >
                  {cat.items.map((g) => (
                    <TheGoi
                      key={g.code}
                      goi={g}
                      chon={maGoi === g.code}
                      onChon={() => doi(() => setMaGoi(g.code))}
                      nhanThoiHan={nhanThoiHan(g.validity_unit, g.validity_count)}
                      gia={tienUsd(g.price_usd)}
                      vnd={tienVnd(g.price_vnd)}
                      soToken={t('banToken.chung.soTokenFox', { so: soVN(g.fox_tokens, locale) })}
                      chuaGia={t('banToken.goiNap.chuaCoGia')}
                    />
                  ))}
                </div>
              )
            }
          </Field>
        )}

        {cat && cheDo === 'tien' && (
          <>
            <Field
              label={t('banToken.yeuCau.oSoTien')}
              required
              hint={
                tokenXemTruoc !== null
                  ? [t('banToken.yeuCau.xemTruocToken', { so: soVN(tokenXemTruoc, locale) }), tienVnd(vndXemTruoc)]
                      .filter(Boolean)
                      .join(' · ')
                  : t('banToken.yeuCau.donGiaLa', { gia: tienUsd(donGia) })
              }
              error={
                loiTien && (daGui || (loiTien !== 'trong' && soTien.trim()))
                  ? t(
                      loiTien === 'trong'
                        ? 'banToken.yeuCau.loiSoTienTrong'
                        : loiTien === 'sai'
                          ? 'banToken.yeuCau.loiSoTienSai'
                          : 'banToken.yeuCau.loiSoTienKhoang',
                    )
                  : loiServer['/amount_usd']
              }
            >
              {(p) => (
                <div className="space-y-2">
                  {cat.muc_nap_usd.length > 0 && (
                    <div className="flex flex-wrap gap-2" role="group" aria-label={t('banToken.yeuCau.mucGoiY')}>
                      {cat.muc_nap_usd.map((m) => {
                        const chon = tien === String(m);
                        return (
                          <Button
                            key={m}
                            variant={chon ? 'primary' : 'outline'}
                            size="sm"
                            aria-pressed={chon}
                            onClick={() => doi(() => setSoTien(String(m)))}
                          >
                            {t('banToken.chung.usd', { so: soVN(m, locale) })}
                          </Button>
                        );
                      })}
                    </div>
                  )}
                  <Input
                    {...p}
                    status={p['aria-invalid'] ? 'error' : undefined}
                    inputMode="decimal"
                    autoComplete="off"
                    className="w-full tabular-nums sm:w-60"
                    suffix={<span className="text-xs text-content-muted">USD</span>}
                    value={soTien}
                    onChange={(e) => doi(() => setSoTien(e.target.value))}
                  />
                </div>
              )}
            </Field>
            <Field label={t('tokenAi.oThoiHan')} required error={loiServer['/duration_count']}>
              {(p) => (
                <Segmented<number>
                  {...p}
                  options={THANG_HOP_LE.map((n) => ({ value: n, label: nhanThoiHan('MONTH', n) }))}
                  value={soThang}
                  onChange={(n) => doi(() => setSoThang(n))}
                />
              )}
            </Field>
          </>
        )}
        {cat && donGia === null && cheDo === 'goi' && (
          <p className="text-xs text-content-muted">{t('banToken.yeuCau.chuaDonGia')}</p>
        )}

        {cheDo === 'gia-han' && (
          <>
            <Field
              label={t('tokenAi.oGoiCanGiaHan')}
              required
              error={kqGiaHan.loi.grantId && daGui ? t('tokenAi.loiChonGoiTamThoi') : loiServer['/grant_id']}
            >
              {(p) => (
                <Select<string>
                  {...p}
                  className="w-full"
                  value={giaHan.grantId || undefined}
                  placeholder={t('tokenAi.chonGoiTamThoi')}
                  onChange={(v) => doi(() => setGiaHan((g) => ({ ...g, grantId: v })))}
                  options={goiGiaHan.map((g) => ({
                    value: g.id,
                    label: t('tokenAi.goiTamThoiNhan', { so: soVN(g.tokens, locale), den: ngayVN(g.valid_to) }),
                  }))}
                />
              )}
            </Field>
            <Field
              label={t('tokenAi.oDenNgayMoi')}
              required
              hint={t('tokenAi.giaHanKhongCongToken')}
              error={
                kqGiaHan.loi.validTo && (daGui || giaHan.validTo)
                  ? loiCapThem.ngay(kqGiaHan.loi.validTo, hanGoiChon)
                  : loiServer['/valid_to']
              }
            >
              {(p) => (
                <ONgayTrongKhoang
                  p={p}
                  value={giaHan.validTo}
                  onChange={(v) => doi(() => setGiaHan((g) => ({ ...g, validTo: v })))}
                  tu={homNay}
                />
              )}
            </Field>
          </>
        )}

        <Field
          label={t(cheDo === 'gia-han' ? 'tokenAi.oLyDo' : 'banToken.yeuCau.oLyDo')}
          required={cheDo === 'gia-han'}
          error={
            lyDoDai
              ? t('tokenAi.loiGhiChuDai', { so: soVN(DO_DAI_GHI_CHU, locale) })
              : cheDo === 'gia-han' && kqGiaHan.loi.reason === 'trong' && daGui
                ? t('tokenAi.loiLyDoTrong')
                : loiServer['/reason']
          }
        >
          {(p) => <Textarea {...p} rows={2} value={lyDo} onChange={(e) => doi(() => setLyDo(e.target.value))} />}
        </Field>
      </form>
    </Dialog>
  );
}

/** Một gói để chọn: radio dạng thẻ. Gói chưa có giá (chưa đặt đơn giá) không chọn được. */
function TheGoi({
  goi,
  chon,
  onChon,
  nhanThoiHan,
  gia,
  vnd,
  soToken,
  chuaGia,
}: {
  goi: TopUpOffer;
  chon: boolean;
  onChon: () => void;
  nhanThoiHan: string;
  gia: string;
  vnd: string | null;
  soToken: string;
  chuaGia: string;
}) {
  const khoa = goi.price_usd === null;
  return (
    <button
      type="button"
      role="radio"
      aria-checked={chon}
      disabled={khoa}
      onClick={onChon}
      className={cn(
        'flex min-w-0 flex-col items-start gap-1 rounded-lg border px-3 py-2.5 text-left transition-colors',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600',
        chon ? 'border-primary-600 bg-primary-50' : 'border-default bg-surface hover:border-strong',
        khoa && 'cursor-not-allowed opacity-60',
      )}
    >
      <span className="w-full truncate text-sm font-medium text-content-primary">{goi.name}</span>
      <span className="text-xs tabular-nums text-content-secondary">
        {soToken} · {nhanThoiHan}
      </span>
      <span className="text-sm tabular-nums text-content-primary">
        {khoa ? chuaGia : gia}
        {vnd && <span className="ml-1.5 text-2xs text-content-muted">{vnd}</span>}
      </span>
    </button>
  );
}
